import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/App';
import {
  ChartBar,
  Folder,
  Buildings,
  FileText,
  Clock,
  CheckCircle,
  TrendUp,
  TrendDown,
  Envelope,
  PenNib,
  Users,
  CurrencyCircleDollar,
  Receipt,
  Archive,
  ChartLineUp,
  Minus
} from '@phosphor-icons/react';

const ZAR = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 });
const fmt = (n) => ZAR.format(Number(n || 0));
const fmtPct = (p) => p == null ? '—' : `${p >= 0 ? '+' : ''}${(p * 100).toFixed(1)}%`;

const STATUS_COLORS_MAP = {
  'New Service Request': '#3B82F6', 'Quotation Accepted': '#6366F1', 'Site Visit Scheduled': '#06B6D4',
  'Assigned to Agent': '#9CA3AF', 'Query Logged': '#F59E0B', 'Under Review': '#F97316', 'Approved': '#10B981', 'Completed': '#4CAF50'
};

const PRIORITY_COLORS_MAP = { 'Low': '#9CA3AF', 'Medium': '#3B82F6', 'High': '#F97316', 'Urgent': '#EF4444' };

const LEAD_COLORS = { 'Cold': '#93C5FD', 'Warm': '#FCD34D', 'Proposal Sent': '#A78BFA', 'Active': '#4CAF50', 'Inactive': '#D1D5DB' };

const BarChart = ({ data, colorMap, maxHeight = 180 }) => {
  const maxVal = Math.max(...Object.values(data), 1);
  return (
    <div className="flex items-end gap-2 justify-around" style={{ height: maxHeight }}>
      {Object.entries(data).map(([label, value]) => (
        <div key={label} className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <span className="text-xs font-semibold text-[#0A192F]">{value}</span>
          <div
            className="w-full max-w-[40px] rounded-t transition-all duration-500"
            style={{
              height: `${Math.max((value / maxVal) * (maxHeight - 30), 4)}px`,
              backgroundColor: colorMap?.[label] || '#4CAF50',
            }}
          />
          <span className="text-[10px] text-gray-500 text-center leading-tight truncate w-full" title={label}>
            {label.length > 10 ? label.slice(0, 9) + '...' : label}
          </span>
        </div>
      ))}
    </div>
  );
};

const TrendChart = ({ data, height = 140 }) => {
  const maxVal = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center gap-1 flex-1">
          <span className="text-[10px] font-medium text-[#0A192F]">{d.count || ''}</span>
          <div
            className="w-full rounded-t transition-all duration-500"
            style={{
              height: `${Math.max((d.count / maxVal) * (height - 30), 2)}px`,
              backgroundColor: d.count > 0 ? '#4CAF50' : '#E5E7EB',
            }}
          />
          <span className="text-[10px] text-gray-400">{d.short}</span>
        </div>
      ))}
    </div>
  );
};

const DeltaBadge = ({ delta }) => {
  if (!delta || delta.pct == null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Minus className="w-3 h-3" /> no prior
      </span>
    );
  }
  const positive = delta.abs >= 0;
  const Icon = positive ? TrendUp : TrendDown;
  const color = positive ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 ${color}`}>
      <Icon className="w-3 h-3" weight="fill" />
      {fmtPct(delta.pct)}
    </span>
  );
};

const FinancialTripleChart = ({ monthly, height = 220 }) => {
  // Show last 12 months as grouped bars (Quoted, Invoiced, Paid)
  const last12 = monthly.slice(-12);
  const maxVal = Math.max(1, ...last12.flatMap(m => [m.quoted, m.invoiced, m.paid]));
  return (
    <div className="w-full">
      <div className="flex items-end gap-2" style={{ height }}>
        {last12.map((m) => (
          <div key={m.month} className="flex flex-col items-center gap-1 flex-1 min-w-0" data-testid={`fin-month-${m.month}`}>
            <div className="flex items-end gap-0.5 w-full justify-center" style={{ height: height - 30 }}>
              <div className="w-1/3 bg-[#0055FF] transition-all" style={{ height: `${(m.quoted / maxVal) * (height - 30)}px` }} title={`Quoted: ${fmt(m.quoted)}`}></div>
              <div className="w-1/3 bg-[#F59E0B] transition-all" style={{ height: `${(m.invoiced / maxVal) * (height - 30)}px` }} title={`Invoiced: ${fmt(m.invoiced)}`}></div>
              <div className="w-1/3 bg-[#4CAF50] transition-all" style={{ height: `${(m.paid / maxVal) * (height - 30)}px` }} title={`Paid: ${fmt(m.paid)}`}></div>
            </div>
            <span className="text-[10px] text-gray-500">{m.short}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs">
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#0055FF] inline-block"></span> Quoted</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#F59E0B] inline-block"></span> Invoiced</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#4CAF50] inline-block"></span> Paid</div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = '#4CAF50', sub }) => (
  <Card data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className="text-3xl font-bold text-[#0A192F] mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className="w-12 h-12 flex items-center justify-center rounded" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminReports = () => {
  const [data, setData] = useState(null);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [{ data: reports }, { data: financial }] = await Promise.all([
          api.get('/dashboard/reports'),
          api.get(`/dashboard/reports/financial${includeArchived ? '?include_archived=true' : ''}`),
        ]);
        setData(reports);
        setFin(financial);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchReports();
  }, [includeArchived]);

  if (loading) {
    return <AdminLayout title="Reports"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  if (!data) return <AdminLayout title="Reports"><p className="text-center text-gray-500 py-12">Failed to load reports</p></AdminLayout>;

  return (
    <AdminLayout title="Reports">
      <div className="space-y-6 fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#0A192F]">Reports & Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Overview of your CRM performance</p>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={Folder} label="Total Cases" value={data.total_cases} color="#3B82F6" />
          <StatCard icon={CheckCircle} label="Completed" value={data.completed_cases} color="#4CAF50" sub={data.total_cases > 0 ? `${Math.round((data.completed_cases / data.total_cases) * 100)}% completion` : ''} />
          <StatCard icon={Clock} label="Avg Resolution" value={`${data.avg_resolution_days}d`} color="#F59E0B" sub="days to complete" />
          <StatCard icon={Buildings} label="Companies" value={data.companies.total} color="#8B5CF6" />
          <StatCard icon={PenNib} label="Signed Contracts" value={data.quotations.signed} color="#10B981" />
        </div>

        {/* ─── Financial Reports ─── */}
        {fin && (
          <div className="space-y-4" data-testid="financial-reports-section">
            <div className="flex items-center gap-2 pt-2">
              <ChartLineUp className="w-5 h-5 text-[#0055FF]" />
              <h2 className="text-lg font-semibold text-[#0A192F]">Financial Reports</h2>
              <Badge variant="outline" className="text-xs rounded-none ml-2">{fin.currency}</Badge>
              <label className="ml-auto flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none" data-testid="include-archived-toggle">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  className="accent-[#0055FF]"
                  data-testid="include-archived-checkbox"
                />
                Include archived / deleted records
                {fin.retention && (
                  <span className="text-gray-400">
                    ({fin.retention.archived_quotations} quotations, {fin.retention.archived_invoices} invoices archived)
                  </span>
                )}
              </label>
            </div>

            {/* Month-on-Month + Year-on-Year */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card data-testid="mom-card">
                <CardHeader className="border-b pb-4">
                  <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                    <ChartBar className="w-5 h-5 text-[#0055FF]" />
                    Month-on-Month
                    <span className="ml-auto text-xs font-normal text-gray-500">
                      {fin.mom.last_month_label || '—'} → {fin.mom.this_month_label}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  {['quoted', 'invoiced', 'paid'].map((k) => {
                    const last = fin.monthly[fin.monthly.length - 1]?.[k] || 0;
                    return (
                      <div key={k} className="flex items-center justify-between" data-testid={`mom-row-${k}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-8 ${k === 'quoted' ? 'bg-[#0055FF]' : k === 'invoiced' ? 'bg-[#F59E0B]' : 'bg-[#4CAF50]'}`}></div>
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500">{k}</p>
                            <p className="text-lg font-bold text-[#0A192F]">{fmt(last)}</p>
                          </div>
                        </div>
                        <DeltaBadge delta={fin.mom[k]} />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card data-testid="yoy-card">
                <CardHeader className="border-b pb-4">
                  <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                    <ChartBar className="w-5 h-5 text-[#0055FF]" />
                    Year-on-Year (YTD vs same window {fin.yoy.last_year_same_window?.label?.replace('Same window ', '') || ''})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  {['quoted', 'invoiced', 'paid'].map((k) => (
                    <div key={k} className="flex items-center justify-between" data-testid={`yoy-row-${k}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-8 ${k === 'quoted' ? 'bg-[#0055FF]' : k === 'invoiced' ? 'bg-[#F59E0B]' : 'bg-[#4CAF50]'}`}></div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-gray-500">{k} (YTD)</p>
                          <p className="text-lg font-bold text-[#0A192F]">{fmt(fin.yoy.ytd?.[k])}</p>
                          <p className="text-xs text-gray-400">vs {fmt(fin.yoy.last_year_same_window?.[k])} last yr</p>
                        </div>
                      </div>
                      <DeltaBadge delta={fin.yoy[k]} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* 12-Month trend */}
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                  <TrendUp className="w-5 h-5 text-[#0055FF]" />
                  12-Month Trend — Quoted vs Invoiced vs Paid
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 pb-4 overflow-x-auto" data-testid="financial-trend-chart">
                <FinancialTripleChart monthly={fin.monthly} />
              </CardContent>
            </Card>

            {/* Top customers + Retention notice */}
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader className="border-b pb-4">
                  <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                    <CurrencyCircleDollar className="w-5 h-5 text-[#4CAF50]" weight="fill" />
                    Top Customers by Revenue (last 12 months)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4" data-testid="top-customers-by-revenue">
                  {fin.top_customers.length > 0 ? (
                    <div className="space-y-2">
                      {fin.top_customers.map((c, i) => (
                        <div key={c.company_id || i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="w-6 h-6 rounded-full bg-[#4CAF50]/10 text-[#4CAF50] flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                            <span className="text-sm text-[#0A192F] truncate">{c.company_name}</span>
                          </div>
                          <div className="text-right ml-2 shrink-0">
                            <p className="text-sm font-bold text-[#0A192F]">{fmt(c.paid_total)}</p>
                            <p className="text-xs text-gray-500">{c.paid_count} invoice{c.paid_count !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 py-6 text-sm">No paid invoices in the last 12 months yet.</p>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="retention-card">
                <CardHeader className="border-b pb-4">
                  <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                    <Archive className="w-5 h-5 text-[#8B5CF6]" weight="fill" />
                    Data Retention
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-3xl font-bold text-[#0A192F]">{fin.retention.years} years</p>
                  <p className="text-xs text-gray-500 mt-1">Financial records retained as required by POPIA &amp; Tax Administration Act.</p>
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Archived quotations</span>
                      <span className="font-bold text-[#0A192F]">{fin.retention.archived_quotations}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Archived invoices</span>
                      <span className="font-bold text-[#0A192F]">{fin.retention.archived_invoices}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Charts Row 1 */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Cases by Status */}
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <ChartBar className="w-5 h-5 text-[#4CAF50]" /> Cases by Status
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6" data-testid="chart-cases-by-status">
              {Object.keys(data.cases_by_status).length > 0 ? (
                <BarChart data={data.cases_by_status} colorMap={STATUS_COLORS_MAP} />
              ) : (
                <p className="text-center text-gray-400 py-8">No data</p>
              )}
            </CardContent>
          </Card>

          {/* Cases by Service Type */}
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <ChartBar className="w-5 h-5 text-[#3B82F6]" /> Cases by Service Type
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6" data-testid="chart-cases-by-type">
              {Object.keys(data.cases_by_type).length > 0 ? (
                <BarChart data={data.cases_by_type} colorMap={{}} />
              ) : (
                <p className="text-center text-gray-400 py-8">No data</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Monthly Trend */}
          <Card className="lg:col-span-2">
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <TrendUp className="w-5 h-5 text-[#4CAF50]" /> Monthly Case Trend (12 months)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6" data-testid="chart-monthly-trend">
              <TrendChart data={data.monthly_trend} />
            </CardContent>
          </Card>

          {/* Cases by Priority */}
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <ChartBar className="w-5 h-5 text-[#F97316]" /> By Priority
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6" data-testid="chart-cases-by-priority">
              {Object.keys(data.cases_by_priority).length > 0 ? (
                <BarChart data={data.cases_by_priority} colorMap={PRIORITY_COLORS_MAP} maxHeight={140} />
              ) : (
                <p className="text-center text-gray-400 py-8">No data</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Top Companies */}
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#8B5CF6]" /> Top Companies
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4" data-testid="top-companies">
              {data.top_companies.length > 0 ? (
                <div className="space-y-3">
                  {data.top_companies.map((c, i) => (
                    <div key={c._id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-[#4CAF50]/10 text-[#4CAF50] flex items-center justify-center text-xs font-bold">{i + 1}</span>
                        <span className="text-sm text-[#0A192F] truncate max-w-[140px]">{c.company_name}</span>
                      </div>
                      <span className="text-sm font-semibold text-[#0A192F]">{c.count} cases</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-4">No data</p>
              )}
            </CardContent>
          </Card>

          {/* Company Lead Status */}
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <Buildings className="w-5 h-5 text-[#F59E0B]" /> Companies by Lead Status
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6" data-testid="chart-companies-lead">
              {Object.keys(data.companies.by_lead_status).length > 0 ? (
                <BarChart data={data.companies.by_lead_status} colorMap={LEAD_COLORS} maxHeight={140} />
              ) : (
                <p className="text-center text-gray-400 py-8">No data</p>
              )}
            </CardContent>
          </Card>

          {/* Quotation & Notification Stats */}
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#10B981]" /> Quick Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4" data-testid="quick-stats">
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Active Contracts</span>
                  <span className="text-sm font-bold text-[#0A192F]">{data.contracts.active}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Total Quotations</span>
                  <span className="text-sm font-bold text-[#0A192F]">{data.quotations.total}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Pending Quotations</span>
                  <span className="text-sm font-bold text-[#F59E0B]">{data.quotations.by_status?.pending || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Emails Sent</span>
                  <span className="text-sm font-bold text-[#4CAF50]">{data.notifications.sent}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-600">Emails Failed</span>
                  <span className="text-sm font-bold text-red-500">{data.notifications.failed}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
